// 统一用户服务 - 自动切换localStorage和API服务
import { 
  UserRecord, 
  UserCard, 
  CreateUserRecordRequest, 
  AssessmentRecord,
  ComprehensiveAssessmentResult 
} from '../types/index';

import { localUserService } from './localUserService';
import { apiUserService } from './apiUserService';
import { authService } from './authService';

// 服务类型
type ServiceMode = 'api' | 'localStorage' | 'hybrid';

class UnifiedUserService {
  private currentMode: ServiceMode = 'hybrid';
  private apiAvailable: boolean | null = null;
  private lastHealthCheck: number = 0;
  private healthCheckInterval = 30000; // 30秒检查一次

  constructor() {
    this.initializeService();
  }

  // 初始化服务
  private async initializeService() {
    console.log('🔧 初始化统一用户服务...');
    await this.checkApiAvailability();
  }

  // 检查API可用性
  private async checkApiAvailability(): Promise<boolean> {
    const now = Date.now();

    if (this.apiAvailable && (now - this.lastHealthCheck) < this.healthCheckInterval) {
      return true;
    }

    try {
      const isHealthy = await apiUserService.checkApiHealth();
      this.lastHealthCheck = now;

      if (isHealthy) {
        if (!this.apiAvailable) {
          console.log('✅ API服务恢复，恢复API模式');
        }
        this.apiAvailable = true;
        this.currentMode = 'api';
        return true;
      }

      console.warn('⚠️ API健康检查失败，保留本地缓存模式');
      this.apiAvailable = false;
      this.currentMode = 'localStorage';
      return false;
    } catch (error) {
      console.warn('⚠️ API健康检查异常，使用本地缓存:', error);
      this.apiAvailable = false;
      this.currentMode = 'localStorage';
      return false;
    }
  }

  // 获取当前使用的服务
  private async getActiveService() {
    const apiAvailable = await this.checkApiAvailability();
    
    if (apiAvailable) {
      return {
        service: apiUserService,
        mode: 'api' as const
      };
    } else {
      return {
        service: localUserService,
        mode: 'localStorage' as const
      };
    }
  }

  // 创建用户档案 - API优先策略
  async createUserRecord(request: CreateUserRecordRequest): Promise<string> {
    // 检查API是否可用
    const apiAvailable = await this.checkApiAvailability();
    
    if (apiAvailable) {
      try {
        // 优先使用API创建
        const userId = await apiUserService.createUserRecord(request);
        console.log('✅ API用户档案创建成功:', userId);
        
        // API成功后，同步到localStorage作为缓存
        try {
          await localUserService.createUserRecord(request, userId);
          console.log('✅ 用户档案已同步到本地缓存');
        } catch (cacheError) {
          console.warn('⚠️ 本地缓存同步失败，不影响主要功能:', cacheError);
        }
        
        return userId;
      } catch (error) {
        console.warn('⚠️ API创建失败，fallback到localStorage:', error);
        // API失败时才使用localStorage
        const fallbackUserId = await localUserService.createUserRecord(request);
        console.log('✅ localStorage fallback成功:', fallbackUserId);
        return fallbackUserId;
      }
    } else {
      // API不可用时直接使用localStorage
      console.log('📱 API不可用，使用localStorage创建用户');
      const userId = await localUserService.createUserRecord(request);
      console.log('✅ localStorage用户档案创建成功:', userId);
      return userId;
    }
  }

  // 获取所有活跃用户的卡片信息 - API优先策略
  async getAllActiveUsers(): Promise<UserCard[]> {
    const apiAvailable = await this.checkApiAvailability();
    
    if (apiAvailable) {
      try {
        // 优先从API获取
        const apiUsers = await apiUserService.getAllActiveUsers();
        console.log(`📋 从API获取到 ${apiUsers.length} 个用户档案`);
        
        // 同时获取localStorage中的用户（可能有API中没有的本地用户）
        const localUsers = await localUserService.getAllActiveUsers();
        console.log(`📋 从localStorage获取到 ${localUsers.length} 个本地用户档案`);
        
        // 合并用户列表，API用户优先，去重
        const allUsers = [...apiUsers];
        const apiUserIds = new Set(apiUsers.map(u => u.id));
        
        // 添加API中没有的本地用户
        localUsers.forEach(localUser => {
          if (!apiUserIds.has(localUser.id)) {
            allUsers.push(localUser);
          }
        });
        
        console.log(`📋 合并后共 ${allUsers.length} 个用户档案 (API: ${apiUsers.length}, 本地独有: ${allUsers.length - apiUsers.length})`);
        return allUsers;
      } catch (error) {
        console.error('❌ API获取用户列表失败，不再使用localStorage缓存:', error);
        // 🚫 移除localStorage回退机制 - 避免显示临时用户数据
        return [];
      }
    } else {
      // 🚫 API不可用时不再使用localStorage - 避免显示临时用户数据
      console.warn('⚠️ API不可用，无法获取用户列表');
      return [];
    }
  }

  // 根据用户ID数组获取多个用户卡片 - API优先策略
  async getUsersByIds(userIds: string[]): Promise<UserCard[]> {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    console.log('🔍 获取用户卡片，ID列表:', userIds);
    const users: UserCard[] = [];
    const apiAvailable = await this.checkApiAvailability();
    
    // 逐个获取用户信息，使用统一的获取逻辑
    for (const userId of userIds) {
      try {
        const userRecord = await this.getUserRecord(userId);
        if (userRecord && userRecord.personalInfo) {
          const userCard: UserCard = {
            id: userRecord.id,
            name: userRecord.personalInfo.name,
            age: userRecord.personalInfo.age,
            gender: userRecord.personalInfo.gender,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(userRecord.personalInfo.name)}&size=128&background=f0f9ff&color=0ea5e9`,
            lastAssessmentDate: userRecord.assessmentHistory?.[0]?.assessmentDate || undefined,
            assessmentCount: userRecord.assessmentHistory?.length || 0,
            status: userRecord.status === 'active' ? 'active' : 'inactive',
            createdAt: userRecord.createdAt
          };
          users.push(userCard);
        }
      } catch (error) {
        console.warn(`⚠️ 获取用户 ${userId} 失败:`, error);
      }
    }
    
    console.log(`✅ 成功获取 ${users.length}/${userIds.length} 个用户卡片 (API ${apiAvailable ? '可用' : '不可用'})`);
    return users;
  }

  // 获取单个用户完整档案 - API优先策略
  async getUserRecord(userId: string): Promise<UserRecord | null> {
    console.log(`🔍 [UnifiedUserService] 获取用户档案: ${userId}`);
    
    const apiAvailable = await this.checkApiAvailability();
    
    if (apiAvailable) {
      try {
        // 优先从API获取
        const userRecord = await apiUserService.getUserRecord(userId);
        console.log('✅ [UnifiedUserService] API获取成功:', userRecord?.personalInfo?.name);
        
        // 将API数据同步到localStorage缓存
        if (userRecord) {
          try {
            // 检查localStorage是否已有此用户，如果没有则添加
            const localRecord = await localUserService.getUserRecord(userId);
            if (!localRecord) {
              // 这里我们只是更新缓存，不创建新用户
              console.log('💾 API用户数据缓存到localStorage');
            }
          } catch (cacheError) {
            console.warn('⚠️ 缓存同步失败，不影响主要功能:', cacheError);
          }
        }
        
        return userRecord;
      } catch (error) {
        console.error('❌ API获取用户档案失败，不再使用localStorage缓存:', error);
        // 🚫 移除localStorage回退机制 - 避免显示临时用户数据
        return null;
      }
    } else {
      // 🚫 API不可用时不再使用localStorage - 避免显示临时用户数据
      console.warn('⚠️ API不可用，无法获取用户档案');
      return null;
    }
  }

  // 获取单个用户完整档案的别名方法（为了兼容性）
  async getUserById(userId: string): Promise<UserRecord | null> {
    return this.getUserRecord(userId);
  }

  // 更新用户档案基本信息
  async updateUserRecord(userId: string, updates: Partial<UserRecord>): Promise<void> {
    const { service, mode } = await this.getActiveService();
    
    try {
      await service.updateUserRecord(userId, updates);
    } catch (error) {
      if (mode === 'api') {
        console.warn('⚠️ API更新用户档案失败，尝试使用localStorage:', error);
        try {
          await localUserService.updateUserRecord(userId, updates);
        } catch (fallbackError) {
          console.error('❌ localStorage fallback也失败:', fallbackError);
          throw fallbackError;
        }
      } else {
        throw error;
      }
    }
  }

  // 为用户添加评估结果
  async addAssessmentResult(
    userId: string, 
    result: ComprehensiveAssessmentResult, 
    notes?: string,
    conductedBy?: string
  ): Promise<void> {
    const { service, mode } = await this.getActiveService();
    
    try {
      await service.addAssessmentResult(userId, result, notes, conductedBy);
    } catch (error) {
      if (mode === 'api') {
        console.warn('⚠️ API添加评估结果失败，尝试使用localStorage:', error);
        try {
          await localUserService.addAssessmentResult(userId, result, notes, conductedBy);
        } catch (fallbackError) {
          console.error('❌ localStorage fallback也失败:', fallbackError);
          throw fallbackError;
        }
      } else {
        throw error;
      }
    }
  }

  // 获取用户评估历史
  async getAssessmentHistory(userId: string): Promise<AssessmentRecord[]> {
    const { service, mode } = await this.getActiveService();
    
    try {
      return await service.getAssessmentHistory(userId);
    } catch (error) {
      if (mode === 'api') {
        console.warn('⚠️ API获取评估历史失败，尝试使用localStorage:', error);
        try {
          return await localUserService.getAssessmentHistory(userId);
        } catch (fallbackError) {
          console.error('❌ localStorage fallback也失败:', fallbackError);
          return [];
        }
      }
      throw error;
    }
  }

  // 删除用户档案（软删除）
  async deactivateUserRecord(userId: string): Promise<void> {
    const { service } = await this.getActiveService();
    await service.deactivateUserRecord(userId);
  }

  // 彻底删除用户档案（慎用）
  async deleteUserRecord(userId: string): Promise<void> {
    const { service } = await this.getActiveService();
    await service.deleteUserRecord(userId);
  }

  // 搜索用户（按姓名）
  async searchUsers(searchTerm: string): Promise<UserCard[]> {
    const { service, mode } = await this.getActiveService();
    
    try {
      const searchResults = await service.searchUsers(searchTerm);
      
      console.log(`🔍 搜索到 ${searchResults.length} 个匹配用户 (${mode})`);
      
      return searchResults;
    } catch (error) {
      if (mode === 'api') {
        console.warn('⚠️ API搜索用户失败，尝试使用localStorage:', error);
        try {
          const fallbackResults = await localUserService.searchUsers(searchTerm);
          
          console.log(`🔍 localStorage fallback搜索到 ${fallbackResults.length} 个匹配用户`);
          
          return fallbackResults;
        } catch (fallbackError) {
          console.error('❌ localStorage fallback也失败:', fallbackError);
          return [];
        }
      }
      throw error;
    }
  }

  // 生成默认头像URL（基于姓名）
  generateDefaultAvatar(name: string, gender: '男' | '女'): string {
    // 优先使用localStorage的实现（更可靠）
    return localUserService.generateDefaultAvatar(name, gender);
  }


  // 获取当前服务模式和状态
  async getServiceStatus(): Promise<{
    mode: ServiceMode;
    apiAvailable: boolean;
    lastHealthCheck: Date;
    currentCarer: any;
    isAuthenticated: boolean;
  }> {
    const apiAvailable = await this.checkApiAvailability();
    const currentCarer = authService.getCurrentCarer();
    
    return {
      mode: this.currentMode,
      apiAvailable,
      lastHealthCheck: new Date(this.lastHealthCheck),
      currentCarer,
      isAuthenticated: authService.isAuthenticated()
    };
  }

  // 强制切换服务模式 (用于测试)
  async forceMode(mode: ServiceMode): Promise<void> {
    console.log(`🔧 强制切换服务模式: ${mode}`);
    this.currentMode = mode;
    
    if (mode === 'api') {
      this.apiAvailable = await apiUserService.checkApiHealth();
    }
  }

  // 数据迁移：从localStorage迁移到API
  async migrateToApi(): Promise<void> {
    try {
      console.log('🔄 开始数据迁移：localStorage -> API');
      
      // 检查API是否可用
      const apiAvailable = await apiUserService.checkApiHealth();
      if (!apiAvailable) {
        throw new Error('API服务不可用，无法进行数据迁移');
      }

      // 获取localStorage中的所有数据
      const localUsers = await localUserService.getAllActiveUsers();
      
      if (localUsers.length === 0) {
        console.log('📭 localStorage中没有用户数据需要迁移');
        return;
      }

      // 准备迁移数据
      const migrationData: any = {
        usersData: [],
        assessmentsData: []
      };

      for (const userCard of localUsers) {
        const fullUser = await localUserService.getUserRecord(userCard.id);
        if (fullUser) {
          migrationData.usersData.push(fullUser);
          
          // 获取评估历史
          const assessmentHistory = await localUserService.getAssessmentHistory(userCard.id);
          migrationData.assessmentsData.push(...assessmentHistory);
        }
      }

      // 执行迁移
      const result = await apiUserService.migrateFromLocalStorage(migrationData);
      
      console.log('✅ 数据迁移完成:', result);
      
      // 迁移成功后清空localStorage (可选)
      // await this.clearLocalStorage();
      
    } catch (error) {
      console.error('❌ 数据迁移失败:', error);
      throw error;
    }
  }

  // 导出所有数据
  async exportAllData(): Promise<any> {
    console.log('📤 导出所有数据...');
    
    const exportData: any = {
      metadata: {
        version: '1.0.0',
        exportDate: new Date().toISOString(),
        source: 'unified-user-service'
      },
      localStorage: {},
      api: {}
    };
    
    try {
      // 导出localStorage数据
      const localUsers = await localUserService.getAllActiveUsers();
      exportData.localStorage.users = localUsers;
      console.log(`📋 导出了${localUsers.length}个localStorage用户`);
    } catch (error) {
      console.warn('⚠️ 无法导出localStorage数据:', error);
      exportData.localStorage.error = error.message;
    }
    
    try {
      // 导出API数据
      if (await this.checkApiAvailability()) {
        const apiUsers = await apiUserService.getAllActiveUsers();
        exportData.api.users = apiUsers;
        console.log(`📋 导出了${apiUsers.length}个API用户`);
      } else {
        exportData.api.error = 'API服务不可用';
      }
    } catch (error) {
      console.warn('⚠️ 无法导出API数据:', error);
      exportData.api.error = error.message;
    }
    
    console.log('✅ 数据导出完成');
    return exportData;
  }

  // 清空localStorage数据 (危险操作)
  async clearLocalStorage(): Promise<void> {
    console.log('⚠️ 清空localStorage数据');
    // 这里可以调用localUserService的清理方法
    localStorage.removeItem('icope_user_index');
    
    // 清空所有用户数据
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('icope_local_users_')) {
        localStorage.removeItem(key);
      }
    }
    
    console.log('🗑️ localStorage数据已清空');
  }
}

// 导出单例实例
export const unifiedUserService = new UnifiedUserService();
export default UnifiedUserService;
